import React from 'react';
import { Plus, Trash2, Settings } from "lucide-react";
import { Button } from "../../../../components/tailwind/button";
import { Label } from "../../../../components/tailwind/label";
import { Input } from "../../../../components/tailwind/input";
import { workflowTheme } from "../../utils/workflowBuilderTheme";
import {
  SESSION_TIMEOUT_MAX_SECONDS,
  SESSION_TIMEOUT_MIN_SECONDS,
} from "../../utils/workflowFlowMapper";
import { validateSessionTimeout } from "../../utils/workflowInspectorUx";

export function WorkflowSettingsPanel({
  intentName,
  setIntentName,
  exampleUtterances,
  setExampleUtterances,
  sessionTimeoutSeconds,
  setSessionTimeoutSeconds,
}) {
  const handleAddUtterance = () => {
    setExampleUtterances([...exampleUtterances, ""]);
  };

  const handleUtteranceChange = (index, value) => {
    const updated = [...exampleUtterances];
    updated[index] = value;
    setExampleUtterances(updated);
  };

  const handleRemoveUtterance = (index) => {
    const updated = exampleUtterances.filter((_, i) => i !== index);
    setExampleUtterances(updated.length > 0 ? updated : [""]);
  };

  return (
    <aside className={`absolute right-0 top-0 bottom-0 z-20 w-[min(100%,400px)] xl:relative xl:z-10 xl:w-[400px] ${workflowTheme.surface} border-l ${workflowTheme.border} flex flex-col h-full shrink-0 shadow-xl xl:shadow-none animate-in slide-in-from-right duration-200`}>
      {/* Header */}
      <div className={`p-4 border-b ${workflowTheme.borderSubtle} flex items-center gap-3 ${workflowTheme.surfaceMuted}`}>
        <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 flex items-center justify-center border border-[#9FE1CB] dark:border-emerald-800">
          <Settings className="w-4 h-4 text-[#0F6E56] dark:text-emerald-400" />
        </div>
        <div>
          <h3 className={`font-semibold ${workflowTheme.textPrimary} text-sm`}>Workflow Settings</h3>
          <span className={`text-[10px] font-bold tracking-wider uppercase ${workflowTheme.textMuted}`}>SETTINGS</span>
        </div>
      </div>

      {/* Body */}
      <div className="p-5 flex-1 overflow-y-auto space-y-6">
        {/* Intent Name */}
        <div className="space-y-2">
          <Label className={`text-xs font-bold ${workflowTheme.textSecondary} uppercase tracking-wide flex items-center gap-1`}>
            Intent Name <span className="text-red-500">*</span>
          </Label>
          <Input
            value={intentName}
            onChange={(e) => {
              const val = e.target.value
                .toLowerCase()
                .replace(/\s+/g, "_")
                .replace(/[^a-z0-9_]/g, "");
              setIntentName(val);
            }}
            placeholder="e.g. check_account_balance"
            className="text-sm h-10 w-full"
          />
        </div>

        {/* Example Utterances */}
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className={`text-xs font-bold ${workflowTheme.textSecondary} uppercase tracking-wide`}>
              Example Utterances <span className="text-red-500">*</span>
            </Label>
            <p className={`text-xs ${workflowTheme.textMuted}`}>
              Provide examples of messages users may send to trigger this intent.
            </p>
          </div>

          <div className="space-y-2.5">
            {exampleUtterances.map((utterance, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <Input
                  value={utterance}
                  onChange={(e) => handleUtteranceChange(idx, e.target.value)}
                  placeholder="e.g. What is my balance?"
                  className="text-sm h-10 flex-1"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 shrink-0"
                  onClick={() => handleRemoveUtterance(idx)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            className="mt-2 text-[#0F6E56] dark:text-emerald-400 border-gray-200 dark:border-slate-800 hover:bg-[#E1F5EE] dark:hover:bg-emerald-950/50 flex items-center gap-1.5 h-9"
            onClick={handleAddUtterance}
          >
            <Plus className="w-4 h-4" />
            Add Example
          </Button>
        </div>

        {/* Session Timeout */}
        <div className="space-y-2">
          <Label className={`text-xs font-bold ${workflowTheme.textSecondary} uppercase tracking-wide`}>
            Session Timeout
          </Label>
          <p className={`text-xs ${workflowTheme.textMuted}`}>
            Maximum idle time in seconds before the workflow session ends.
          </p>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={SESSION_TIMEOUT_MIN_SECONDS}
              max={SESSION_TIMEOUT_MAX_SECONDS}
              value={sessionTimeoutSeconds}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '') {
                  setSessionTimeoutSeconds('');
                  return;
                }
                const parsed = parseInt(val, 10);
                if (!Number.isNaN(parsed)) {
                  setSessionTimeoutSeconds(parsed);
                }
              }}
              className={`text-sm h-10 w-28 ${workflowTheme.input}`}
            />
            <span className={`text-sm ${workflowTheme.textSecondary}`}>seconds</span>
          </div>
          {validateSessionTimeout(sessionTimeoutSeconds) && (
            <p className="text-xs text-red-500 font-medium mt-1">
              {validateSessionTimeout(sessionTimeoutSeconds)}
            </p>
          )}
        </div>

      </div>
    </aside>
  );
}
